import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../utils/store';
import { contentService } from '../../services/apiService';
import { toast } from 'react-toastify';
import { FiBarChart2, FiTrendingUp, FiFileText, FiDollarSign, FiMic, FiVolume2 } from 'react-icons/fi';
import { useT } from '../../utils/i18n';

export const Dashboard = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const t = useT();

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const result = await contentService.getUserStats('month');
      setStats(result);
    } catch (error) {
      toast.error(t('toast.statsFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const contentTypeLabel = (type) => {
    const key = `type.${type}`;
    if (t(key) !== key) return t(key);
    return type
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/([0-9]+)/g, ' $1')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const StatCard = ({ icon: Icon, label, value, subtext, accent = 'primary' }) => (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 bg-${accent}-100 rounded-lg`}>
          <Icon size={24} className={`text-${accent}-600`} />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary-500" />
      </div>
    );
  }

  const hasMediaUsage = (stats?.audioMinutes > 0) || (stats?.ttsCharacters > 0);

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="card bg-gradient-to-r from-primary-50 to-secondary-50">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('dashboard.welcome')}, {user?.name}!
        </h1>
        <p className="text-gray-600">{t('dashboard.subtitle')}</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FiFileText}
          label={t('dashboard.totalRequests')}
          value={stats?.totalRequests || 0}
          subtext={t('dashboard.thisMonth')}
        />
        <StatCard
          icon={FiTrendingUp}
          label="Text Tokens"
          value={(stats?.textTokens || 0).toLocaleString()}
          subtext={t('dashboard.used')}
        />
        <StatCard
          icon={FiDollarSign}
          label={t('dashboard.totalCost')}
          value={`$${(stats?.totalCost || 0).toFixed(4)}`}
          subtext={t('dashboard.usd')}
        />
        <StatCard
          icon={FiBarChart2}
          label={t('dashboard.successRate')}
          value={`${(stats?.successRate || 0).toFixed(1)}%`}
          subtext={t('dashboard.successful')}
        />
      </div>

      {/* Audio usage (only shown when there's dubbing activity) */}
      {hasMediaUsage && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stats.audioMinutes > 0 && (
            <div className="card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Audio Processed</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {stats.audioMinutes.toFixed(1)} <span className="text-lg font-medium text-gray-500">min</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Language change — audio analysis</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FiMic size={24} className="text-blue-600" />
                </div>
              </div>
            </div>
          )}
          {stats.ttsCharacters > 0 && (
            <div className="card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Audio Generated</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {stats.ttsCharacters.toLocaleString()}
                    <span className="text-lg font-medium text-gray-500 ml-1">chars</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Language change — Telugu audio</p>
                </div>
                <div className="p-3 bg-violet-100 rounded-lg">
                  <FiVolume2 size={24} className="text-violet-600" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content Type Breakdown */}
      {stats?.byContentType && Object.keys(stats.byContentType).length > 0 && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('dashboard.usageByType')}</h2>
          <div className="space-y-4">
            {Object.entries(stats.byContentType).map(([type, data]) => (
              <div key={type} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{contentTypeLabel(type)}</p>
                  <p className="text-sm text-gray-600">
                    {data.count} {t('dashboard.requests')} · {data.tokens.toLocaleString()} {t('dashboard.tokens')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">${data.cost.toFixed(4)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
